import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import OutcomeModalV2Cst from "@components/OutcomeModalV2Cst";
import UserWizard from "../Wizard/UserWizard";
import { useSaveUser } from "../Wizard/useSaveUser";
import {
  CUSTOM_FIELD_KEYS,
  INITIAL_USER_WIZARD_DATA,
  UserWizardData,
} from "../Wizard/types";
import {
  CustomFieldsCatalog,
  UserCustomFieldsProvider,
} from "../Wizard/customFieldsContext";

const AddUser: React.FC = () => {
  const { id: corporateId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("user-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups`;
  const [submitting, setSubmitting] = useState(false);

  const { dataDetail: dataCustomFields } = useFetchDataDetail(
    `${ServicesURL.getCorporateCustomFields}?corporate_id=${corporateId}`,
    PagesID["corporate.customFields"],
  );

  const { outcome, openOutcome, setOpenOutcome, save, isUserSaved } =
    useSaveUser({
      mode: "create",
      corporateId,
      initialStaticGroupIds: [],
    });

  const catalog: CustomFieldsCatalog = useMemo(() => {
    const raw = dataCustomFields?.catalog || {};
    const out: CustomFieldsCatalog = {};
    CUSTOM_FIELD_KEYS.forEach((k) => {
      if (raw[k]) out[k] = raw[k];
    });
    return out;
  }, [dataCustomFields]);

  const onComplete = async (data: UserWizardData) => {
    setSubmitting(true);
    try {
      await save(data);
    } finally {
      setSubmitting(false);
    }
  };

  if (!dataCustomFields) return <FormLoadingStateCst />;

  return (
    <UserCustomFieldsProvider catalog={catalog}>
      <UserWizard
        mode="create"
        initialData={INITIAL_USER_WIZARD_DATA}
        backTo={backTo}
        onComplete={onComplete}
        isCompleting={submitting}
      />
      <OutcomeModalV2Cst
        open={openOutcome}
        outcome={outcome}
        successTitle={t("outcome.createSuccessTitle")}
        errorTitle={t("outcome.errorTitle")}
        confirmLabel={t("outcome.goToUsersList")}
        onClose={() => {
          setOpenOutcome(false);
          if (isUserSaved) navigate(backTo);
        }}
        onConfirm={() => {
          setOpenOutcome(false);
          navigate(backTo);
        }}
      />
    </UserCustomFieldsProvider>
  );
};

export default AddUser;
